import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export function useApi() {
  const { getToken } = useAuth();

  async function request(method, path, body = null) {
    const token = await getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const opts = { method, headers };
    if (body) opts.body = JSON.stringify(body);

    const res = await fetch(`${API_BASE}${path}`, opts);
    const data = await res.json();

    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  }

  return {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    del: (path) => request('DELETE', path),

    // Brackets
    getBrackets: () => request('GET', '/brackets'),
    getBracket: (id) => request('GET', `/brackets/${id}`),
    createBracket: (data) => request('POST', '/brackets', data),
    updateBracket: (id, data) => request('PUT', `/brackets/${id}`, data),
    deleteBracket: (id) => request('DELETE', `/brackets/${id}`),

    // Scores
    getScores: () => request('GET', '/scores'),
    getLiveScores: () => request('GET', '/scores/live'),

    // Leaderboard
    getLeaderboard: () => request('GET', '/leaderboard'),

    // Teams
    getTeams: () => request('GET', '/teams'),
    getSleeperTeams: () => request('GET', '/teams/sleeper-eligible'),

    // Admin
    adminGetStats: () => request('GET', '/admin/stats'),
    adminGetBrackets: () => request('GET', '/admin/brackets'),
    adminGetGames: () => request('GET', '/admin/games'),
    adminBackupGames: (label) => request('POST', '/admin/games/backup', { label }),
    adminGetBackup: () => request('GET', '/admin/games/backup'),
    adminRestoreGames: () => request('POST', '/admin/games/restore', {}),
    adminRecalculate: () => request('POST', '/admin/recalculate'),
    adminPoll: () => request('POST', '/admin/poll'),
    adminDeleteBracket: (id) => request('DELETE', `/admin/brackets/${id}`),
    adminGetBracket: (id) => request('GET', `/admin/brackets/${id}`),
    adminUpdateBracketPicks: (id, data) => request('PUT', `/admin/brackets/${id}/picks`, data),
    adminUpdateGame: (id, data) => request('PUT', `/admin/games/${id}`, data),
    recalculateScores: () => request('POST', '/admin/recalculate', {}),
    adminOverrideGame: (id, data) => request('PUT', `/admin/games/${id}`, data),
    adminGetUsers: () => request('GET', '/admin/users'),
    adminUpdateUser: (uid, data) => request('PUT', `/admin/users/${uid}`, data),
    adminSetPassword: (uid, password) => request('POST', `/admin/users/${uid}/set-password`, { password }),
    adminDeleteUser: (uid) => request('DELETE', `/admin/users/${uid}`),
    adminSetDeadlines: (data) => request('PUT', '/admin/deadlines', data),
    adminGetDeadlines: () => request('GET', '/admin/deadlines'),
    getAllTeams: () => request('GET', '/admin/teams'),
    getBracketWiring: () => request('GET', '/admin/setup/wiring'),
    runVerify: () => request('GET', '/admin/setup/verify'),
    searchEspnTeams: (search) => request('GET', `/admin/espn/teams?search=${encodeURIComponent(search)}`),
    getAllEspnTeams: () => request('GET', '/admin/espn/teams/all'),
    getDeadlines: () => fetch(`${API_BASE}/deadlines`).then(r => r.json()),
    getBranding: () => request('GET', '/admin/branding'),
    saveBranding: (data) => request('POST', '/admin/branding', data),
    getPollerSchedule: () => request('GET', '/admin/poller/schedule'),
    savePollerSchedule: (data) => request('POST', '/admin/poller/schedule', data),
    pollNow: () => request('POST', '/admin/poller/poll-now', {}),
    saveBracketWiring: (data) => request('POST', '/admin/setup/wiring', data),

    // ESPN Teams Cache
    updateEspnTeamsCache: () => request('POST', '/admin/espn-teams/update', {}),
    getEspnTeamsCacheStatus: () => request('GET', '/admin/espn-teams/status'),

    // Payment
    getPaymentConfig: () => fetch(`${API_BASE}/payment-config`).then(r => r.json()),
    adminGetPaymentConfig: () => request('GET', '/admin/payment-config'),
    adminSavePaymentConfig: (data) => request('PUT', '/admin/payment-config', data),
    adminSetBracketPaid: (id, paid) => request('PUT', `/admin/brackets/${id}/paid`, { paid }),

    // Payouts
    adminGetPayoutConfig: () => request('GET', '/admin/payout-config'),
    adminSavePayoutConfig: (data) => request('PUT', '/admin/payout-config', data),

    // Archive
    getArchiveYears: () => request('GET', '/archive'),
    getMyBracketHistory: () => request('GET', '/brackets/history'),
    getArchiveYear: (year) => request('GET', `/archive/${year}`),
    saveArchiveYear: (year, data) => request('POST', `/archive/${year}`, data),
    deleteArchiveYear: (year) => request('DELETE', `/archive/${year}`),
  };
}
